import type { PropsWithChildren, RefObject } from 'react';

import { useNavTheme } from '#/lib/nav-theme';
import { cn } from '#/lib/utils';
import { Link } from '#/components/Link';

import { TranscendLogo } from '../TranscendLogo';

/**
 *
 * Conditionally render a link component or a div component based on the value of `isMenuBarHidden`.
 * This is used to hide the logo and menu bar when the menu bar is hidden.
 *
 * @param className - The class name to apply to the wrapper element.
 * @param isMenuBarHidden - Whether the menu bar is hidden.
 * @param children - The content to render inside the wrapper.
 * @returns A div element if `isMenuBarHidden` is true, otherwise a link element linking to the home page.
 */
const LogoWrapper: React.FC<
  PropsWithChildren<{ isMenuBarHidden: boolean; className: string }>
> = ({ className, isMenuBarHidden, children }) => {
  if (isMenuBarHidden) {
    return <div className={className}>{children}</div>;
  } else {
    return (
      <Link className={className} href="/">
        {children}
      </Link>
    );
  }
};

/**
 * The header wrapper component is responsible for rendering the header and conditionally rendering its contents.
 * It takes in various props, such as `headerRef`, `isMenuBarHidden`, and `isMobileNavOpen`.
 * The component conditionally renders a div or a link based on the value of `isMenuBarHidden`.
 * The div element is used to hide the logo and menu bar when the menu bar is hidden.
 * The link element is used to link to the home page when the menu bar is visible.
 *
 * @param className - The class name to apply to the header wrapper element.
 * @param headerRef - A reference to the header element.
 * @param isMenuBarHidden - Whether the menu bar is hidden.
 * @param isMobileNavOpen - Whether the mobile navigation menu is open.
 * @returns The header wrapper component with the appropriate content based on the props.
 */
export const HeaderWrapper: React.FC<
  PropsWithChildren<{
    className?: string;
    headerRef: RefObject<HTMLElement | null>;
    isMenuBarHidden: boolean;
    isMobileNavOpen: boolean;
  }>
> = ({ children, headerRef, isMenuBarHidden, className, isMobileNavOpen }) => {
  const { theme } = useNavTheme();

  return (
    <header id="site-header" ref={headerRef} className={className}>
      <div
        className={cn(
          'relative flex h-56 items-center justify-between rounded-15 border-1 border-grayscale-02 bg-cloud px-20 transition-[border-radius] ml:mx-auto ml:h-full ml:max-w-[1500px] ml:border-none ml:bg-[unset] ml:px-50',
          {
            'rounded-b-0': isMobileNavOpen,
          },
        )}
      >
        <LogoWrapper
          isMenuBarHidden={isMenuBarHidden}
          className="z-1 block transition-opacity"
        >
          {/*
            Mobile bar is always cloud → Cipher 5.
            Desktop: white on purple surfaces; Cipher 5 on light + charcoal.
          */}
          <TranscendLogo
            className={cn(
              'h-30 transition-colors text-cipher ml:h-40',
              theme === 'purple' ? 'ml:text-cloud' : 'ml:text-cipher',
            )}
          />
        </LogoWrapper>

        {isMenuBarHidden ? null : children}
      </div>
    </header>
  );
};
